import { ReactElement, useState, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  Divider,
  FormControl,
  FormLabel,
  InputBase,
} from '@mui/material';
import { eventActions } from '../../redux/slices/event.slice';
import { useDropzone } from 'react-dropzone';
import { EEventCreateSteps } from '../../types/@event';
import { IFiles } from '../../types/@shared';
import { RootState } from '../../redux/store';
import { useNavigate } from 'react-router-dom';

import { styles } from './event-create-form.styles';
import { ROUTES } from '../../helpers/constans';
import ButtonWithLoading from '../button-with-loading';
import { IOrganisationEventItem } from '../../types/@organisation';

interface IProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  organisationScoped?: boolean;
  onCreateNewStyle?: () => void;
}

const getLatestEvent = (events: IOrganisationEventItem[]): IOrganisationEventItem | null => {
  if (!events.length) {
    return null;
  }

  return [...events].sort((left, right) => {
    const leftValue = Number(left.sortValue || left.eventStartTime || 0);
    const rightValue = Number(right.sortValue || right.eventStartTime || 0);
    return rightValue - leftValue;
  })[0] || null;
};

const EventCreateForm = ({ setStep, organisationScoped = false, onCreateNewStyle }: IProps): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formValues = useSelector((state: RootState) => state.events.eventCreate.eventForm);
  const selectedTeam = useSelector((state: RootState) => state.events.eventCreate.team);
  const organisationEvents = useSelector((state: RootState) => state.organisations.events);
  const selectedOrganisation = useSelector((state: RootState) => state.events.eventCreate.organisation);
  const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
  const isCreated = useSelector((state: RootState) => state.events.isCreated);

  const [fileLogo, setFileLogo] = useState<IFiles>({ image: '' });
  const [fileSponsorLogo, setFileSponsorLogo] = useState<IFiles>({ image: '' });

  const latestTeamEvent = useMemo(() => {
    const organisationId = String(selectedOrganisation?._id || '').trim();
    const teamId = String(selectedTeam?._id || '').trim();

    if (!organisationScoped || !organisationEvents || !organisationId || !teamId) {
      return null;
    }

    if (String(organisationEvents.organisationId || '').trim() !== organisationId) {
      return null;
    }

    const teamEntry = organisationEvents.teams.find((team) => String(team.teamId || '').trim() === teamId);
    if (!teamEntry) {
      return null;
    }

    return getLatestEvent(teamEntry.events);
  }, [organisationEvents, organisationScoped, selectedOrganisation?._id, selectedTeam?._id]);

  useEffect(() => {
    if (!latestTeamEvent) {
      return;
    }

    const needsUpdate =
      !formValues.twitterId ||
      !formValues.instagramId ||
      !formValues.tiktokId;

    if (!needsUpdate) {
      return;
    }

    dispatch(
      eventActions.eventCreateForm({
        ...formValues,
        twitterId: formValues.twitterId || latestTeamEvent.twitterId || '',
        instagramId: formValues.instagramId || latestTeamEvent.instagramId || '',
        tiktokId: formValues.tiktokId || latestTeamEvent.tiktokId || '',
      })
    );
  }, [dispatch, formValues, latestTeamEvent]);

  const handleDropLogo = useCallback((acceptedFiles: any) => {
    setFileLogo(() => ({ image: acceptedFiles[0] }));
  }, []);

  const handleDropSponsorLogo = useCallback((acceptedFiles: any) => {
    setFileSponsorLogo(() => ({ image: acceptedFiles[0] }));
  }, []);

  const {
    getRootProps: getRootPropsLogo,
    getInputProps: getInputPropsLogo,
    isDragActive: isDragActiveLogo,
    open: openLogo,
    acceptedFiles: acceptedFilesLogo,
  } = useDropzone({
    noClick: true,
    onDrop: handleDropLogo,
  });

  const {
    getRootProps: getRootPropsSponsorLogo,
    getInputProps: getInputPropsSponsorLogo,
    isDragActive: isDragActiveSponsorLogo,
    open: openSponsorLogo,
    acceptedFiles: acceptedFilesSponsorLogo,
  } = useDropzone({
    noClick: true,
    onDrop: handleDropSponsorLogo,
  });

  const updateForm = (name: string, value: string): void => {
    dispatch(
      eventActions.eventCreateForm({
        ...formValues,
        [name]: value,
      })
    );
  };

  const handleChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const target = event.target as typeof event.target & {
      name: string;
      value: string;
    };
    updateForm(target.name, target.value);
  };

  const handleBack = (): void => {
    if (formValues.contextType === 'team_hierarchy') {
      setStep(EEventCreateSteps.team);
      return;
    }

    if (organisationScoped) {
      setStep(EEventCreateSteps.type);
      return;
    }

    if (formValues.contextType === 'organisation') {
      setStep(EEventCreateSteps.organisation);
      return;
    }

    setStep(EEventCreateSteps.type);
  };

  const handleSave = (): void => {
    setIsLoadingBtn(() => true);
    const formData = new FormData();

    if (fileLogo.image) {
      formData.append('images', fileLogo.image, 'logo');
    }

    if (fileSponsorLogo.image) {
      formData.append('images', fileSponsorLogo.image, 'sponsorLogo');
    }

    dispatch(eventActions.putEvent(formData));
  };

  useEffect(() => {
    if (isCreated) {
      dispatch(eventActions.isCreated(false));
      setIsLoadingBtn(() => false);
      navigate(ROUTES.events);
    }
  }, [isCreated, navigate, dispatch]);

  return (
    <Box sx={styles.mainBox}>
      <Box sx={styles.titleBox}>Add the actual information about your event</Box>

      {latestTeamEvent ? (
        <Typography sx={{ maxWidth: '1100px', margin: '0 auto 20px', color: '#6b7c8f', px: 3 }}>
          Social IDs are prefilled from the latest event for {selectedTeam?.name || 'this team'} and can be changed here.
        </Typography>
      ) : null}

      <Box sx={styles.contentBox}>
        <Box sx={styles.contentImage}>
          <Typography sx={styles.contentTitle}>Logo</Typography>
          <Box
            sx={styles.dropzone(
              acceptedFilesLogo[0] && URL.createObjectURL(acceptedFilesLogo[0]),
              isDragActiveLogo
            )}
            {...getRootPropsLogo()}
            onClick={openLogo}
          >
            <input {...getInputPropsLogo()} />
            {!acceptedFilesLogo[0] && (
              <Typography sx={styles.dropzoneText}>
                Drag 'n' drop some files here, or click to select files
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={styles.contentImage}>
          <Typography sx={styles.contentTitle}>Sponsor logo</Typography>
          <Box
            sx={styles.dropzone(
              acceptedFilesSponsorLogo[0] && URL.createObjectURL(acceptedFilesSponsorLogo[0]),
              isDragActiveSponsorLogo
            )}
            {...getRootPropsSponsorLogo()}
            onClick={openSponsorLogo}
          >
            <input {...getInputPropsSponsorLogo()} />
            {!acceptedFilesSponsorLogo[0] && (
              <Typography sx={styles.dropzoneText}>
                Drag 'n' drop some files here, or click to select files
              </Typography>
            )}
          </Box>
        </Box>

        <Divider orientation="vertical" sx={styles.divider} />

        <Box sx={styles.formBox} component="form" onChange={handleChange}>
          <FormControl sx={styles.form}>
            <FormLabel sx={styles.formLabel}>Name</FormLabel>
            <InputBase sx={styles.input} name="name" value={formValues.name} />
          </FormControl>

          <FormControl sx={styles.form}>
            <FormLabel sx={styles.formLabel}>Slogan</FormLabel>
            <InputBase sx={styles.input} name="slogan" value={formValues.slogan} />
          </FormControl>

          <FormControl sx={styles.form}>
            <FormLabel sx={styles.formLabel}>Link</FormLabel>
            <InputBase sx={styles.input} name="link" value={formValues.link} />
          </FormControl>

          <FormControl sx={styles.form}>
            <FormLabel sx={styles.formLabel}>X ID</FormLabel>
            <InputBase sx={styles.input} name="twitterId" value={formValues.twitterId} />
          </FormControl>

          <FormControl sx={styles.form}>
            <FormLabel sx={styles.formLabel}>Instagram ID</FormLabel>
            <InputBase sx={styles.input} name="instagramId" value={formValues.instagramId} />
          </FormControl>

          <FormControl sx={styles.form}>
            <FormLabel sx={styles.formLabel}>Tiktok ID</FormLabel>
            <InputBase sx={styles.input} name="tiktokId" value={formValues.tiktokId} />
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', margin: '0 auto', maxWidth: '1100px', width: '100%', px: 3, pb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleBack}>Back</Button>
          {organisationScoped && onCreateNewStyle ? (
            <Button variant="outlined" onClick={onCreateNewStyle}>
              New style event
            </Button>
          ) : null}
        </Box>

        <ButtonWithLoading
          buttonName="Save"
          styles={{}}
          isLoading={isLoadingBtn}
          handler={handleSave}
        />
      </Box>
    </Box>
  );
};

export default EventCreateForm;
